import {
	Box,
	Button,
	ButtonGroup,
	ClickAwayListener,
	Grow,
	MenuItem,
	MenuList,
	Paper,
	Popper,
	Theme,
} from '@mui/material';
import React, { useState, useRef } from 'react';
import { RiBankCardLine } from 'react-icons/ri';
import { useTranslation } from 'services/localization';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { createStyles, makeStyles } from '@mui/styles';
import Spinner from 'common/components/Spinner';

export type ChargeButtonType =
	| 'MARK_AS_PAID'
	| 'RENTLE_POS_PAY'
	| 'SEND_PAYMENT_TO_TEKSO_POS'
	| 'SEND_PAYMENT_TO_CLOUD_POS';

export type PaymentButtonType = ChargeButtonType;

interface Props<T extends PaymentButtonType[]> {
	types: T;
	onClick: (type: T[number]) => void;
	loading: boolean;
	variant: 'contained' | 'outlined';
}

const PaymentButton = <T extends PaymentButtonType[]>(props: Props<T>) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const [buttonMenuOpen, setButtonMenuOpen] = useState(false);
	const anchorRef = useRef(null);
	const selectedType = props.types[0];

	const getTypeTranslation = (type: PaymentButtonType) => {
		const typesTranslationMap: Record<PaymentButtonType, string | JSX.Element> = {
			MARK_AS_PAID: t('common:payment.markAsPaid', 'Mark as paid'),
			RENTLE_POS_PAY: (
				<>
					<Box mr={1}>
						<RiBankCardLine />
					</Box>{' '}
					{t('common:payment.pay', 'Pay')}
				</>
			),
			SEND_PAYMENT_TO_TEKSO_POS: `${t('actions.sendToPos', 'Send to POS')}`,
			SEND_PAYMENT_TO_CLOUD_POS: `${t('actions.sendToPos', 'Send to POS')}`,
		};
		return typesTranslationMap[type];
	};

	const handleToggle = () => {
		setButtonMenuOpen((prevOpen) => !prevOpen);
	};

	const handleMenuItemClick = (type: PaymentButtonType) => {
		setButtonMenuOpen(false);
		props.onClick(type);
	};

	const showBtnDropdown = props.types.length > 1;
	const whiteRightBorder = props.variant === 'contained' && showBtnDropdown;

	return (
		<Box sx={{ position: 'relative' }}>
			<ButtonGroup
				fullWidth
				variant={props.variant}
				color="primary"
				size="small"
				ref={anchorRef}
				disabled={props.loading}
			>
				<Button
					onClick={() => props.onClick(selectedType)}
					sx={{
						borderRightColor: (theme) =>
							whiteRightBorder ? `${theme.palette.common.white} !important` : undefined,
					}}
				>
					{props.loading ? <Spinner padding={8} /> : getTypeTranslation(selectedType)}
				</Button>
				{showBtnDropdown && (
					<Button
						size="small"
						aria-controls={buttonMenuOpen ? 'split-button-menu' : undefined}
						aria-expanded={buttonMenuOpen ? 'true' : undefined}
						aria-label="select merge strategy"
						aria-haspopup="menu"
						onClick={handleToggle}
						sx={{ flex: 0 }}
					>
						<ArrowDropDownIcon />
					</Button>
				)}
			</ButtonGroup>
			<Popper
				open={buttonMenuOpen}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
				placement="bottom-start"
				className={classes.popper}
			>
				{({ TransitionProps }) => (
					<Grow {...TransitionProps}>
						<Paper classes={{ root: classes.menuPaper }}>
							<ClickAwayListener onClickAway={() => setButtonMenuOpen(false)}>
								<MenuList>
									{props.types.map((option) => (
										<MenuItem
											value={option}
											key={option}
											onClick={() => handleMenuItemClick(option)}
										>
											{getTypeTranslation(option)}
										</MenuItem>
									))}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		menuPaper: {
			width: '100%',
		},
		popper: {
			width: '100%',
			zIndex: 2,
		},
	}),
);

export default PaymentButton;
